import React, { useState, useEffect, useCallback } from 'react';
import { inventoryService, productsService } from '../services';
import DashboardLayout from '../components/DashboardLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import { formatCurrency, formatDate } from '../utils/formatters';
import PageHeader from '../components/ui/PageHeader';
import StatGrid from '../components/ui/StatGrid';
import FilterPanel from '../components/ui/FilterPanel';
import ListCard, { ListCards, CardAction } from '../components/ui/ListCard';
import Icon from '../components/nav/Icon';

const Inventory = () => {
  const [expenses, setExpenses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingExpense, setDeletingExpense] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    category: '',
    productId: '',
    expenseType: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    expenseType: '',
    description: '',
    amount: '',
    quantity: '1',
    productId: '',
    category: '',
    date: new Date().toISOString().split('T')[0]
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const filterParams = {};
      if (filters.startDate) filterParams.startDate = filters.startDate;
      if (filters.endDate) filterParams.endDate = filters.endDate;
      if (filters.category) filterParams.category = filters.category;
      if (filters.productId) filterParams.productId = filters.productId;
      if (filters.expenseType) filterParams.expenseType = filters.expenseType;

      const data = await inventoryService.getAll(filterParams);
      setExpenses(data || []);
    } catch (err) {
      console.error('Error fetching expenses:', err);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const filterParams = {};
      if (filters.startDate) filterParams.startDate = filters.startDate;
      if (filters.endDate) filterParams.endDate = filters.endDate;

      const data = await inventoryService.getStats(filterParams);
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [filters]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await productsService.getAll(1, 100);
      setProducts(response.products || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
    fetchStats();
    fetchProducts();
  }, [fetchExpenses, fetchStats, fetchProducts]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      category: '',
      productId: '',
      expenseType: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.expenseType.trim()) {
      errors.expenseType = 'Expense type is required';
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = 'Amount must be greater than 0';
    }
    if (formData.quantity && parseFloat(formData.quantity) <= 0) {
      errors.quantity = 'Quantity must be greater than 0';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const expenseData = {
        ...formData,
        amount: parseFloat(formData.amount),
        quantity: parseFloat(formData.quantity) || 1,
        productId: formData.productId || null,
        category: formData.category || null
      };

      if (editingExpense) {
        await inventoryService.update(editingExpense.id, expenseData);
      } else {
        await inventoryService.create(expenseData);
      }

      setShowAddModal(false);
      setEditingExpense(null);
      resetForm();
      fetchExpenses();
      fetchStats();
    } catch (err) {
      console.error('Error saving expense:', err);
      setFormErrors({ submit: err.message || 'Failed to save expense' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      expenseType: '',
      description: '',
      amount: '',
      quantity: '1',
      productId: '',
      category: '',
      date: new Date().toISOString().split('T')[0]
    });
    setFormErrors({});
  };

  const handleAddExpense = () => {
    setEditingExpense(null);
    resetForm();
    setShowAddModal(true);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setFormData({
      expenseType: expense.expenseType || '',
      description: expense.description || '',
      amount: expense.amount?.toString() || '',
      quantity: expense.quantity?.toString() || '1',
      productId: expense.productId || '',
      category: expense.category || '',
      date: expense.date ? new Date(expense.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setShowAddModal(true);
  };

  const handleDeleteExpense = (expense) => {
    setDeletingExpense(expense);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await inventoryService.delete(deletingExpense.id);
      setShowDeleteModal(false);
      setDeletingExpense(null);
      fetchExpenses();
      fetchStats();
    } catch (err) {
      console.error('Error deleting expense:', err);
    }
  };

  // Get unique categories and expense types from expenses
  const categories = [...new Set(expenses.map(e => e.category).filter(Boolean))];

  const activeFilters = Object.values(filters).filter(Boolean).length;

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6">
        <PageHeader
          title="Inventory"
          subtitle="Packaging and other inventory expenses"
          actions={
            <button
              type="button"
              onClick={handleAddExpense}
              aria-label="Add expense"
              className="h-10 w-10 md:w-auto md:px-4 inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              <Icon name="plus" className="w-5 h-5" />
              <span className="hidden md:inline">Add expense</span>
            </button>
          }
        />

        {stats && (
          <StatGrid
            items={[
              { label: 'Total spent', value: formatCurrency(stats.totalAmount), sub: `${stats.expenseCount} items` },
              { label: 'Avg expense', value: formatCurrency(stats.averageExpense), sub: 'Per item' },
              { label: 'Total quantity', value: stats.totalQuantity.toLocaleString(), sub: 'Units' },
              { label: 'Avg unit cost', value: formatCurrency(stats.averageUnitCost), sub: 'Per unit' },
            ]}
          />
        )}

        {/* Filters */}
        <FilterPanel search={null} activeCount={activeFilters} columns="lg:grid-cols-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expense Type</label>
              <input
                type="text"
                name="expenseType"
                value={filters.expenseType}
                onChange={handleFilterChange}
                placeholder="Filter by type"
                className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
              <select
                name="productId"
                value={filters.productId}
                onChange={handleFilterChange}
                className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Products</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>{product.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full h-11 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                Clear Filters
              </button>
            </div>
        </FilterPanel>

        {/* Expenses Table */}
        <div className={!loading && expenses.length > 0
          ? 'md:bg-white md:rounded-lg md:shadow md:overflow-hidden'
          : 'bg-white rounded-2xl border border-slate-200 md:border-0 md:rounded-lg md:shadow overflow-hidden'}>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner />
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No expenses found</h3>
              <p className="text-gray-500">Get started by adding your first inventory expense.</p>
            </div>
          ) : (
            <>
            <ListCards>
              {expenses.map((expense) => {
                const productName = expense.productId ? products.find(p => p.id === expense.productId)?.name : null;
                return (
                  <ListCard
                    key={expense.id}
                    title={expense.expenseType}
                    subtitle={[expense.description, productName].filter(Boolean).join(' · ') || undefined}
                    amount={formatCurrency(expense.amount)}
                    meta={`${formatDate(expense.date)} · ${expense.quantity} × ${formatCurrency(expense.unitCost)}${expense.category ? ` · ${expense.category}` : ''}`}
                    actions={
                      <>
                        <CardAction tone="primary" onClick={() => handleEditExpense(expense)}>Edit</CardAction>
                        <CardAction tone="danger" onClick={() => handleDeleteExpense(expense)}>Delete</CardAction>
                      </>
                    }
                  />
                );
              })}
            </ListCards>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Cost</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {expenses.map((expense) => (
                    <tr key={expense.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(expense.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {expense.expenseType}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {expense.description || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {expense.category || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {expense.productId ? products.find(p => p.id === expense.productId)?.name || '-' : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {expense.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        {formatCurrency(expense.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {formatCurrency(expense.unitCost)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEditExpense(expense)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteExpense(expense)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

        {/* Add/Edit Modal */}
        <Modal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setEditingExpense(null);
            resetForm();
          }}
          title={editingExpense ? 'Edit Expense' : 'Add Expense'}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expense Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="expenseType"
                value={formData.expenseType}
                onChange={handleInputChange}
                placeholder="e.g., Packaging Materials, Labels, Boxes"
                className={`w-full border rounded-md px-3 py-2 ${formErrors.expenseType ? 'border-red-500' : 'border-gray-300'}`}
                required
              />
              {formErrors.expenseType && (
                <p className="mt-1 text-sm text-red-500">{formErrors.expenseType}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="w-full border-gray-300 rounded-md"
                placeholder="Additional details about this expense"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className={`w-full border rounded-md px-3 py-2 ${formErrors.amount ? 'border-red-500' : 'border-gray-300'}`}
                  required
                />
                {formErrors.amount && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.amount}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  step="1"
                  min="1"
                  className={`w-full border rounded-md px-3 py-2 ${formErrors.quantity ? 'border-red-500' : 'border-gray-300'}`}
                />
                {formErrors.quantity && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.quantity}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="e.g., Packaging, Supplies"
                  className="w-full border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link to Product (Optional)</label>
              <select
                name="productId"
                value={formData.productId}
                onChange={handleInputChange}
                className="w-full border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">No Product Link</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>{product.name}</option>
                ))}
              </select>
            </div>

            {formErrors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-700">{formErrors.submit}</p>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingExpense(null);
                  resetForm();
                }}
                className="w-full sm:w-auto h-12 sm:h-10 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto h-12 sm:h-10 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : editingExpense ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeletingExpense(null);
          }}
          onConfirm={confirmDelete}
          title="Delete Expense"
          message={`Are you sure you want to delete this expense: ${deletingExpense?.expenseType}?`}
        />
      </div>
    </DashboardLayout>
  );
};

export default Inventory;

